import AppExclusiveContent from "@blocks/AppNativeBanner/AppExclusiveContent";
import RenderArticleBlocks from "@blocks/RenderArticleBlocks/RenderArticleBlocks";
import MainContainer, { AdsSettings } from "@container/OutbrainMainContainer";
import SideBlock from "@container/SideBlock/SideBlock";
import WrapperBlock from "@container/WrapperBlock";
import Taboola from "@elements/Advertisement/Taboola";
import { AdsImu2 } from "@elements/Advertisement/variant/AdsImu";
import useGlobalAdSetting from "@hooks/useGlobalAdSetting";
import { useGrapeshotData } from "@hooks/useGrapeshot";
import { useWindowSize } from "@hooks/useWindowSize/useWindowSize";
import ArticleMetaData from "@pages/Article/ArticleMetaData";
import ErrorPage from "@pages/ErrorPage";
import NotFound from "@pages/NotFound";
import {
  CustomContext,
  TRouteWithoutRedirect,
  useRouteContext,
} from "@sphtech/web2-core/ssr";
import { ResponseType } from "@sphtech/web2-core/ssr";
import {
  ArticleAdPageTargetingType,
  PageAdTargetingTypeEnum,
} from "@typings/Ads.d";
import { ArticlePayloadResponseType } from "@typings/PagePayloadType";
import { ReactElement } from "react";

export default function TmPicks(): ReactElement {
  const routeContext: CustomContext<
    TRouteWithoutRedirect<ArticlePayloadResponseType, string>
  > = useRouteContext();
  const dataLoaderResponse = routeContext.context;
  const adsSettings: AdsSettings = useGlobalAdSetting();
  const { gsChannels } = useGrapeshotData(
    dataLoaderResponse.type === ResponseType.SUCCESS
      ? (dataLoaderResponse.payload.article.urlPath as string)
      : "",
  );
  const { isScreenLG } = useWindowSize();

  if (
    dataLoaderResponse.type === ResponseType.CLIENT_ERROR ||
    dataLoaderResponse.type === ResponseType.SERVER_ERROR
  ) {
    return dataLoaderResponse.type === ResponseType.CLIENT_ERROR ? (
      <NotFound />
    ) : (
      <ErrorPage message={dataLoaderResponse.payload ?? ""} />
    );
  }

  const pageAdTargetValue = PageAdTargetingTypeEnum.ARTICLE;

  const mainArticle = dataLoaderResponse.payload.article;
  const sectionName = mainArticle.sectionName;
  const epaperCoverImageUrl =
    dataLoaderResponse.payload.epaperCoverImage.coverImageUri || "";

  const articlePageTarget: ArticleAdPageTargetingType[] = [
    { key: "page_number", value: "1" },
    { key: "tmarticleid", value: mainArticle.id },
    {
      key: "tmtags",
      value: mainArticle.tags?.map((tags) => tags.name).join(", ") || "",
    },
  ];

  const { gaData, metaTags } = ArticleMetaData({
    article: mainArticle,
    isMainArticle: true,
    gsChannels: gsChannels,
  });

  const pageUrl = mainArticle.urlPath;

  return (
    <MainContainer
      metaTagsProps={metaTags}
      gaData={gaData}
      sectionName={sectionName}
      pageAdTargetValue={pageAdTargetValue}
      suppliedAdsSettings={adsSettings}
      gsChannels={gsChannels}
    >
      <div id={mainArticle.id} className="article-card">
        <WrapperBlock className="hover:text flex flex-col lg:flex-row justify-between">
          <AppExclusiveContent
            title={mainArticle.title}
            sectionName={sectionName}
          />

          {isScreenLG ? (
            <SideBlock
              sectionName={sectionName}
              pageAdTargetValue={pageAdTargetValue}
              articlePageTarget={articlePageTarget}
              gsChannels={gsChannels}
              promoteEpaper={true}
              epaperCoverImageUrl={epaperCoverImageUrl}
            />
          ) : (
            <>
              {/* Mobile infinite scroll view */}
              {/* Render first block on default article */}
              <RenderArticleBlocks
                index={0}
                pageNumber={0}
                pageUrl={mainArticle.urlPath ? mainArticle.urlPath : ""}
                hasTaboola={adsSettings.isTaboolaEnabled}
                articleData={mainArticle}
                pageAdTargetValue={pageAdTargetValue}
                gsChannels={gsChannels}
              />
              {/* Render Taboola block on first article */}
              {adsSettings.isTaboolaEnabled && (
                <div className="pt-5 border-t border-pink-300">
                  <Taboola
                    counter={0}
                    pathName={pageUrl}
                    pageType="article"
                    mode="thumbnails-base-widget"
                    container="below-article-thumbnails"
                    placement="Below Article Thumbnails"
                    targetType="mix"
                    isFlush={true}
                  />
                </div>
              )}
              <div className="border-y border-solid border-pink-300 mt-5">
                <AdsImu2
                  sectionName={sectionName}
                  pageAdTargetType={pageAdTargetValue}
                  rootClassName="mt-5"
                  articlePageTarget={articlePageTarget}
                  gsChannels={gsChannels}
                />
              </div>
            </>
          )}
        </WrapperBlock>
      </div>
    </MainContainer>
  );
}
